import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Truck, Plus } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useStickyState } from "@/lib/stickyState";   // 29 Oct 2026 — filters survive back / refresh / rotate
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Button, Spinner, EmptyState, KPI, Input, Select } from "@/components/ui";
import { type DeliveryRow, DELIVERY_SELECT } from "./deliveries";
import { DeliveryCard } from "./ProjectMaterialsTab";

// =============================================================================
// DeliveriesRegisterScreen — /materials  (manager+)
// =============================================================================
// Every delivery / pickup taken, newest first. The two things a manager comes
// here for: deliveries nobody has matched to a job yet (the slip's project
// number didn't match a project's client_project_ref), and matched
// deliveries not yet ticked in the client's tracker — APD gives two days
// from delivery for that (brain: "Telstra FIM deliveries via APD"). Piece 2
// turns the second into a nudge; for now the register shows the age.
// =============================================================================

type Filter = "all" | "unmatched" | "to_tick" | "ticked";

export default function DeliveriesRegisterScreen() {
  const user = useRequireAuth();
  const [filter, setFilter] = useStickyState<Filter>("deliveries:filter", "all");
  const [q, setQ] = useStickyState("deliveries:q", "");
  // 29 Oct 2026 — James: "some small filters and visual polish to see deliveries on different clients and/or projects … we get a lot of these"
  const [clientId, setClientId] = useStickyState<string>("deliveries:client", "all");
  const [projectId, setProjectId] = useStickyState<string>("deliveries:project", "all");
  const [days, setDays] = useStickyState<number>("deliveries:days", 90);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["deliveries"],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("material_deliveries").select(DELIVERY_SELECT)
        .order("received_at", { ascending: false }).limit(500);
      if (error) throw error;
      return (data ?? []) as unknown as DeliveryRow[];
    },
  });

  const unmatched = rows.filter(r => !r.project_id);
  const toTick = rows.filter(r => r.project_id && r.status !== "ticked_in_client_sheet");
  const overdue = toTick.filter(r => Date.now() - new Date(r.received_at).getTime() > 2 * 24 * 3600 * 1000);

  const clients = useMemo(() => { const m = new Map<string, string>(); rows.forEach(r => { const c = r.project?.client; if (c) m.set(c.client_id, c.legal_name); }); return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1])); }, [rows]);
  const projectsOpt = useMemo(() => { const m = new Map<string, string>(); rows.forEach(r => { if (r.project_id && r.project && (clientId === "all" || r.project.client?.client_id === clientId)) m.set(r.project_id, `${r.project.project_code} · ${r.project.project_name}`); }); return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1])); }, [rows, clientId]);

  const shown = useMemo(() => {
    let list = rows;
    if (clientId !== "all") list = list.filter(r => r.project?.client?.client_id === clientId);
    if (projectId !== "all") list = list.filter(r => r.project_id === projectId);
    if (days > 0) { const cutoff = Date.now() - days * 24 * 3600 * 1000; list = list.filter(r => new Date(r.received_at).getTime() >= cutoff || !r.project_id); }
    if (filter === "unmatched") list = list.filter(r => !r.project_id);
    if (filter === "to_tick")   list = list.filter(r => r.project_id && r.status !== "ticked_in_client_sheet");
    if (filter === "ticked")    list = list.filter(r => r.status === "ticked_in_client_sheet");
    const needle = q.trim().toLowerCase();
    if (needle) {
      list = list.filter(r => [
        r.client_project_ref, r.client_site_code, r.client_site_name, r.docket_number, r.supplier, r.carrier, r.signed_by, r.notes,
        r.project?.project_code, r.project?.project_name,
        ...(r.lines ?? []).flatMap(l => [l.part_number, l.client_material_no, l.description, ...(l.serials ?? [])]),
      ].some(v => (v ?? "").toLowerCase().includes(needle)));
    }
    return list;
  }, [rows, filter, q, clientId, projectId, days]);

  if (!user) return <div className="p-8"><Spinner /></div>;
  if (!hasRole(user, "manager")) {
    return <div className="p-8 text-text-muted text-sm">The deliveries register is for managers. Field users log deliveries from <Link to="/capture/delivery" className="text-accent">Take a delivery</Link>.</div>;
  }

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-wider2 text-text-muted">Materials</div>
          <h1 className="text-2xl font-semibold tracking-tight">Deliveries</h1>
          <p className="text-text-muted text-[13px] mt-1">Everything taken at the depot, picked up from a client warehouse or delivered to site. Match it to the job, then tick the client's tracker and mark it here.</p>
        </div>
        <div className="flex items-center gap-2"><Link to="/materials/bom"><Button variant="secondary">Site BOMs</Button></Link><Link to="/capture/delivery"><Button icon={Plus}>Take a delivery</Button></Link></div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <KPI label="Deliveries" value={String(rows.length)} icon={Truck} />
        <KPI label="Not matched to a job" value={String(unmatched.length)} sub="pick the job on each" />
        <KPI label="To tick in client sheet" value={String(toTick.length)} sub={overdue.length ? `${overdue.length} older than 2 days` : "APD: 2 days from delivery"} accent={overdue.length > 0} />
        <KPI label="Ticked" value={String(rows.length - unmatched.length - toTick.length)} />
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        {([["all", "All"], ["unmatched", `Not matched${unmatched.length ? ` · ${unmatched.length}` : ""}`], ["to_tick", `To tick${toTick.length ? ` · ${toTick.length}` : ""}`], ["ticked", "Ticked"]] as [Filter, string][]).map(([k, label]) => (
          <button key={k} type="button" onClick={() => setFilter(k)}
            className={`px-3 py-1.5 rounded-full text-[12px] border transition-colors ${filter === k ? "bg-accent/15 border-accent text-accent" : "border-line text-text-muted hover:text-text"}`}>{label}</button>
        ))}
        <span className="w-px h-5 bg-line mx-1" />
        <select value={clientId} onChange={e => { setClientId(e.target.value); setProjectId("all"); }} className="bg-ink-raised border border-line rounded px-2 py-1.5 text-[12px] text-text max-w-[200px]">
          <option value="all">All clients</option>
          {clients.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </select>
        <select value={projectId} onChange={e => setProjectId(e.target.value)} className="bg-ink-raised border border-line rounded px-2 py-1.5 text-[12px] text-text max-w-[260px]">
          <option value="all">All jobs</option>
          {projectsOpt.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </select>
        <select value={String(days)} onChange={e => setDays(Number(e.target.value))} className="bg-ink-raised border border-line rounded px-2 py-1.5 text-[12px] text-text" title="Unmatched dockets always show">
          <option value="30">Last 30 days</option><option value="90">Last 90 days</option><option value="365">Last year</option><option value="0">Everything</option>
        </select>
        <div className="flex-1 min-w-[200px]"><Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search project no., site, docket, part number, serial…" /></div>
      </div>
      <div className="text-[11px] text-text-dim mb-3">{shown.length} of {rows.length} · lines are folded — open a card to see what was on the docket</div>

      {isLoading && <Spinner />}
      {!isLoading && shown.length === 0 && <EmptyState icon={Truck} title="Nothing here" description={rows.length ? "No deliveries match that filter." : "No deliveries logged yet."} />}
      <div className="space-y-3">
        {shown.map(r => <DeliveryCard key={r.delivery_id} row={r} canManage showProject compact invalidateKeys={[["deliveries"], ["project_deliveries", r.project_id ?? ""]]} />)}
      </div>
    </div>
  );
}
